import { clsx, estyled } from "@/utils/styled";
import { theme } from "@/utils/theme";
import { joinURL } from "@/utils/url";
import { defineComponent, Fragment, h, type PropType } from "vue";

// Individual toast notification
const toastNotificationContainerClass = estyled.cssClass`
  background: ${theme.colors.brand900};
  background-opacity: 0.8;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(40px);
  color: white;
  border: 0;
  position: relative;
  border-radius: 8px 8px 0 0;
  font-weight: 500;
  word-break: break-words;
  display: flex; 
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  padding: 1.5rem 1rem 1.5rem 1rem;
  overflow: hidden;
`;

const toastNotificationContainerPaddedClass = estyled.cssClass`
  padding-top: 2rem;
`;

const closeButtonClass = estyled.cssClass`
  color: white;
  font-size: 1.125rem;
  line-height: 1;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  cursor: pointer;
  background: none;
  border: none;
  flex-shrink: 0;
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const actionButtonClass = estyled.cssClass`
  display: flex;
  align-self: center;
  padding: 16px 24px;
  align-items: center;
  gap: 8px;
  border-radius: 500px;
  box-shadow: 0 3px 6px 0 rgba(46, 46, 41, 0.16);
  background: ${theme.colors.brand600};

  color: #F4F0FF;
  text-align: center;
  font-family: ${theme.fonts.sans};
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;

  &:hover {
    background: ${theme.colors.brand700};
  }
`;

const toastContainerClass = estyled.cssClass`
  display: flex;
  flex-direction: column;
  min-height: 40px;
`;

const aggregatePointsClass = estyled.cssClass`
  background: linear-gradient(208deg, #FFE280 0%, #CC9E00 145.65%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: Inter;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px; /* 80% */
  text-transform: capitalize;
  text-align: center;
`;

const rankingPositionClass = estyled.cssClass`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: 8px;
  position: relative;
`;

const rankingPositionBadgeClass = estyled.cssClass`
  width: 4rem;
  height: 4rem;
  position: absolute;
  right: -0.25rem;
`;

const rankingPositionProgressClass = estyled.cssClass`
  position: relative;
  width: 100%;
  height: 1.5rem;
  margin: 1.25rem 0 0 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.30);
  display: flex;
  align-items: center;
`;

const rankingPositionProgressBarClass = estyled.cssClass`
  transition: width 0.3s ease-in-out;
  height: 1.5rem;
  border-radius: 8px;
  background: linear-gradient(208deg, #FFE280 0%, #CC9E00 145.65%);
`;

const rankingPositionProgressTextClass = estyled.cssClass`
  font-family: ${theme.fonts.sans};
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 700;
  margin-right: 2rem;
  line-height: 80%;
  text-transform: capitalize;
  background: linear-gradient(208deg, #FFE280 0%, #CC9E00 145.65%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const rankingPositionDescriptionClass = estyled.cssClass`
  font-family: ${theme.fonts.sans};
  font-size: 1rem;
  color: #F4F0FF;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  text-align: center;
`;

export const ToastNotificationContainer = defineComponent({
  setup: (props, { slots }) => {
    return () => {
      return h(
        "div",
        {
          class: [
            toastNotificationContainerClass,
            !slots.default && toastNotificationContainerPaddedClass,
          ],
        },
        [
          slots.default &&
            h("div", { class: toastContainerClass }, slots.default?.()),
          h(
            "button",
            {
              class: closeButtonClass,
              onClick: props.onClose,
            },
            "×",
          ),
          slots.actions?.(),
        ],
      );
    };
  },
  props: {
    points: {
      type: Number as PropType<number>,
      required: true,
    },
    onClose: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },
  slots() {
    return "Hi";
  },
});

export const ToastNotificationClaimPoints = defineComponent({
  setup: (props) => {
    return () => {
      return h(Fragment, null, [
        h(
          "p",
          {
            class: aggregatePointsClass,
          },
          `total: ${props.points} points`,
        ),
        h(
          "button",
          {
            class: actionButtonClass,
            onClick: props.onAction,
          },
          props.label,
        ),
      ]);
    };
  },
  props: {
    label: {
      type: String as PropType<string>,
      required: true,
    },
    points: {
      type: Number as PropType<number>,
      required: true,
    },
    onAction: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },
});

export const ToastNotificationRankingPosition = defineComponent({
  setup: (props) => {
    return () => {
      const pointsToGo = Math.max(props.targetPoints - props.currentPoints, 0);
      return h(Fragment, null, [
        h(
          "div",
          {
            class: rankingPositionClass,
          },
          [
            h("div", { class: rankingPositionProgressClass }, [
              h("div", {
                class: rankingPositionProgressBarClass,
                style: {
                  width: `${Math.min(
                    (props.currentPoints / props.targetPoints) * 100,
                    100,
                  )}%`,
                },
              }),
              h("img", {
                class: rankingPositionBadgeClass,
                src: joinURL(
                  import.meta.env.BASE_URL,
                  "img",
                  "top-5-badge.png",
                ),
              }),
            ]),
            h("div", { class: rankingPositionProgressTextClass }, [
              h("p", { class: rankingPositionProgressTextClass }, [
                `${props.currentPoints} / ${Math.max(
                  props.targetPoints,
                  props.currentPoints,
                )}`,
              ]),
            ]),
          ],
        ),
        h(
          "p",
          {
            class: rankingPositionDescriptionClass,
          },
          pointsToGo > 0
            ? `Just ${pointsToGo} points to reach the top 5%`
            : "You're in the top 5%",
        ),
      ]);
    };
  },
  props: {
    currentPoints: {
      type: Number as PropType<number>,
      required: true,
    },
    targetPoints: {
      type: Number as PropType<number>,
      required: true,
    },
  },
});
